import { getBackendUrl } from "@Utils";
import React, { useState } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { getStatusBtnStyle } from "./ReviewsHelper";
import { convertDate } from "@Services/luxonHelper";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import type { Creator } from "@ServerTypes/reviewflows/reviewflows.schema";

/**
 * One entry of a review resource's `statuses` array. SERVER GAP: `reviewResourceResultSchema`
 * (reviews.schema.ts) declares no `statuses` field at all, and `resourceInReviewItemSchema.statuses`
 * only declares `{ name? }` under `.passthrough()`, so there is no server type to flow. Shape taken
 * from the `resources.$.statuses` writes in `changeResourceStatusInReview`.
 */
export interface ResourceStatusEntry {
  _id?: string;
  name?: string;
  category?: string;
  comment?: string;
  createdDate?: number;
  createdBy?: Creator;
  signatureId?: string | null;
}

/**
 * One entry of a review resource's `signatures` array, written by `addSignatureToResources`.
 * SERVER GAP: also absent from `reviewResourceResultSchema`.
 */
export interface ResourceSignatureEntry {
  createdBy?: Creator;
  createdDate?: number;
  signatureId?: string | null;
}

interface ResourceStatusHistoryInReviewProps {
  readonly statuses: ResourceStatusEntry[];
  readonly closeModal: () => void;
  readonly signatures?: ResourceSignatureEntry[];
  readonly showSignatureHistory?: boolean;
}

function ResourceStatusHistoryInReview({
  statuses,
  closeModal,
  signatures = [],
  showSignatureHistory = false,
}: ResourceStatusHistoryInReviewProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("statusChanges");

  const activeTabChangeHandler = (e: React.MouseEvent<HTMLElement>) => {
    // `getAttribute` is the typed accessor for a named attribute (`NamedNodeMap` declares only a
    // numeric index signature, so index access there needs an assertion). Every element this
    // handler is bound to carries `data-name`, so the null branch is unreachable in practice.
    const target = e.currentTarget.getAttribute("data-name");

    if (target) {
      setActiveTab(target);
    }
  };

  const getFormattedDate = (date?: number | null) => {
    if (date) {
      return convertDate(Number(date), "dateForText");
    }

    return "-";
  };

  const getSignatureBtn = (signatureId?: string | null) => {
    let url: string | undefined = void 0;
    let target = "_self";

    if (signatureId) {
      url = `${getBackendUrl()}/fileSystemServer/getSignDocs/${signatureId}`;
      target = "_blank";
    }

    return (
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
        <a href={url} target={target}>
          {t("View E-Signature")}
        </a>
      </ButtonComponent>
    );
  };

  const getUserNameComponent = (user: Creator) => (
    <div>
      <UserNameTagComponent id={user.userId} />
    </div>
  );

  const header = () => (
    <div className="flex flex-row items-center justify-between w-full rounded-md">
      <p className="text-lg font-bold">{t("Activity")}</p>
    </div>
  );

  const statusChangesContent = () => (
    <div className="ps-6 pt-4 pe-4 pb-4 overflow-auto">
      {statuses.length > 1 ? (
        statuses.map((status: ResourceStatusEntry, index: number) => {
          if (index) {
            const { name, category, createdDate, createdBy, comment, signatureId } = status;

            const lastStatus = statuses[index - 1]!;

            const currentStatusStyle = getStatusBtnStyle(category);
            const lastStatusStyle = getStatusBtnStyle(lastStatus.category);

            return (
              <div key={index} className="flex flex-col gap-3 pb-8">
                <div className="flex flex-row items-center justify-start gap-2 py-0.5">
                  <p>{t("Status changed")}</p>
                  {index > 1 && <p>{t("from")}</p>}
                  {index > 1 && (
                    <span
                      className="text-sm py-1.5 px-4 rounded border-0 text-white font-[var(--font-weight-600)]"
                      style={lastStatusStyle}
                    >
                      {lastStatus.name}
                    </span>
                  )}
                  <p>{t("to")}</p>
                  <span
                    className="text-sm py-1.5 px-4 rounded border-0 text-white font-[var(--font-weight-600)]"
                    style={currentStatusStyle}
                  >
                    {name}
                  </span>
                </div>

                <div className="flex flex-row items-center justify-start gap-2 py-0.5">
                  <p>{t("By")}</p>
                  {/* KNOWN BUG, preserved verbatim: a status entry's `createdBy` is dereferenced
                      (`user.userId`) without a guard, so a legacy status row written without it
                      throws. Does the server read it? N/A — read path; `changeResourceStatusInReview`
                      always writes `createdBy`. Fix: render "-" when absent. */}
                  {getUserNameComponent(createdBy!)}
                  <p>{t("on")}</p>
                  <p className="font-[var(--font-weight-700)] text-base">{getFormattedDate(createdDate)}</p>
                </div>

                {comment && <div className="p-2 bg-[#f0f5f9] rounded text-sm">{comment}</div>}

                {signatureId && <div>{getSignatureBtn(signatureId)}</div>}
              </div>
            );
          }
        })
      ) : (
        <p>{t("There is no status history for the resource")}</p>
      )}
    </div>
  );

  const signaturesContent = () => (
    <div className="flex flex-col gap-8 ps-6 pe-4 pt-4 pb-4 overflow-auto">
      {signatures.length ? (
        signatures.map((signature: ResourceSignatureEntry, index: number) => {
          const { createdBy, createdDate, signatureId } = signature;

          return (
            <div key={index} className="flex flex-col gap-2">
              <p className="flex flex-row items-center gap-1">
                {/* KNOWN BUG, preserved verbatim: same unconditional `createdBy` deref for signature rows. */}
                {getUserNameComponent(createdBy!)}
                <span>{t("added their signature to the file on")}</span>
                <span className="font-[var(--font-weight-700)] text-base">{getFormattedDate(createdDate)}</span>
              </p>
              <div>{getSignatureBtn(signatureId)}</div>
            </div>
          );
        })
      ) : (
        <p>{t("No signatures have been added to this resource")}</p>
      )}
    </div>
  );

  const tabComponent = () => {
    const className1 =
      activeTab === "statusChanges"
        ? "rounded-md bg-[linear-gradient(96deg,_var(--primary-gradient-color1)_0%,_var(--primary-gradient-color2)_110%)] h-1"
        : "bg-inherit h-1";
    const className2 =
      activeTab === "signatures"
        ? "rounded-md bg-[linear-gradient(96deg,_var(--primary-gradient-color1)_0%,_var(--primary-gradient-color2)_110%)] h-1"
        : "bg-inherit h-1";

    return (
      <div
        className="flex flex-row items-center justify-content-start border border-system_colour_44 border-t-0 ps-6 gap-6"
        style={{ paddingBottom: "0.125rem" }}
      >
        <div
          data-name="statusChanges"
          className="flex flex-col cursor-pointer h-14 justify-end [&_span]:leading-[2.75rem] [&_span]:text-base [&_span]:font-semibold"
          onClick={activeTabChangeHandler}
        >
          <span>{t("Status Changes")}</span>
          <div className={className1} />
        </div>

        {showSignatureHistory && (
          <div
            data-name="signatures"
            className="flex flex-col cursor-pointer h-14 justify-end [&_span]:leading-[2.75rem] [&_span]:text-base [&_span]:font-semibold"
            onClick={activeTabChangeHandler}
          >
            <span>{t("Signatures")}</span>
            <div className={className2} />
          </div>
        )}
      </div>
    );
  };

  const content = () => (
    <>
      {activeTab === "statusChanges" && statusChangesContent()}
      {activeTab === "signatures" && showSignatureHistory && signaturesContent()}
    </>
  );

  const modalContent = () => (
    <div className="h-600 overflow-auto flex flex-col">
      {tabComponent()}
      {content()}
    </div>
  );

  return (
    <ModalStateDialogComponent
      isVisible
      width="43.75rem"
      headerTitle={header()}
      modelContent={modalContent()}
      onCancel={closeModal}
      allowModalPadding={false}
    />
  );
}

export default ResourceStatusHistoryInReview;
